// pages/RegistroOcupantes.js
import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Space,
  Typography,
  message,
  Tag,
  Tooltip,
  Row,
  Col,
  Card,
  DatePicker,
  Statistic,
  Badge,
  Alert,
  Spin,
  Descriptions,
  Empty,
  Tabs,
  InputNumber,
  Checkbox,
  Divider
} from 'antd';
import {
  FileTextOutlined,
  DownloadOutlined,
  SearchOutlined,
  ReloadOutlined,
  UserOutlined,
  TeamOutlined,
  CalendarOutlined,
  FlagOutlined,
  IdcardOutlined,
  EyeOutlined,
  InfoCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  GlobalOutlined,
  MailOutlined,
  PhoneOutlined,
  HomeOutlined,
  ExportOutlined
} from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';
import PageContainer from '../components/PageContainer';
import { BASE_URL } from '../utils/constants';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;

const RegistroOcupantes = () => {
  // Estados
  const [ocupantes, setOcupantes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generando, setGenerando] = useState(false);
  const [fechaSeleccionada, setFechaSeleccionada] = useState(dayjs());
  const [modalVisible, setModalVisible] = useState(false);
  const [detalleOcupante, setDetalleOcupante] = useState(null);
  const [configuracion, setConfiguracion] = useState({
    tipoPolicia: 0,
    codigoMossos: '',
    numeroFichero: '001'
  });
  const [form] = Form.useForm();
  const [resumenVisible, setResumenVisible] = useState(false);
  const [filteredInfo, setFilteredInfo] = useState({});
  const [sortedInfo, setSortedInfo] = useState({});

  // Cargar configuración inicial
  useEffect(() => {
    cargarConfiguracion();
    cargarOcupantes();
  }, []);

  const cargarConfiguracion = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BASE_URL}/api/opciones/hotel`, {
        headers: { Authorization: `Bearer ${token}` }
      });      
      
      // console.log(" response => ",response.data.data[0].RegistroFichero);
      
      if (response.data.success) {
        setConfiguracion({
          tipoPolicia: response.data.data[0].TipoPolicia || 0,
          codigoMossos: response.data.data[0].CodigoMossos || '',
          numeroFichero: String(response.data.data[0].RegistroFichero || 1).padStart(3, '0')
        });
        form.setFieldsValue({
          tipoPolicia: response.data.data[0].TipoPolicia || 0,
          numeroFichero: String(response.data.data[0].RegistroFichero || 1).padStart(3, '0')
        });
      }
    } catch (error) {
      console.error('Error cargando configuración:', error);
    }
  };

  const cargarOcupantes = async (fecha) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const fechaStr = fecha || fechaSeleccionada.format('DD/MM/YYYY');
      
      const response = await axios.get(`${BASE_URL}/api/registroocupantes/registropolicia`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { fecha: fechaStr }
      });

      if (response.data.success) {
        const data = response.data.data || [];
        setOcupantes(data);
        
        // Calcular estadísticas
        const nacionalidades = {};
        const tipoDoc = {};
        let total = data.length;
        
        data.forEach(item => {
          const nacion = item.nacion || 'DESCONOCIDO';
          nacionalidades[nacion] = (nacionalidades[nacion] || 0) + 1;
          
          const tipo = item.TIpoDocumento || 'Sin especificar';
          const tipoMap = {
            '01': 'DNI',
            '02': 'Pasaporte',
            '03': 'NIE',
            '06': 'Identificación'
          };
          const tipoNombre = tipoMap[tipo] || tipo;
          tipoDoc[tipoNombre] = (tipoDoc[tipoNombre] || 0) + 1;
        });
        
      }
    } catch (error) {
      console.error('Error cargando ocupantes:', error);
      message.error('Error al cargar los ocupantes');
    } finally {
      setLoading(false);
    }
  };

  const handleBuscar = () => {
    cargarOcupantes();
  };

  const handleCambioFecha = (date) => {
    setFechaSeleccionada(date || dayjs());
  };

  const handleVerDetalle = (record) => {
    setDetalleOcupante(record);
    setModalVisible(true);
  };

  const handleGenerarFichero = async () => {
    try {
      const values = await form.validateFields();
      setGenerando(true);
      
      const token = localStorage.getItem('token');
      const fechaStr = fechaSeleccionada.format('DD/MM/YYYY');
      
      const response = await axios.post(
        `${BASE_URL}/api/registroocupantes/generar-fichero`,
        {
          fecha: fechaStr,
          numeroFichero: values.numeroFichero || '001',
          tipoPolicia: values.tipoPolicia
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        const { nombreFichero, contenido, totalOcupantes } = response.data.data;
        
        // Crear y descargar el fichero
        const blob = new Blob([contenido], { type: 'text/plain;charset=utf-8' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = nombreFichero;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        message.success(`Fichero "${nombreFichero}" generado con ${totalOcupantes} ocupantes`);
        
        // Mostrar resumen
        setResumenVisible(true);
        
        // Incrementar número de fichero
        const nuevoNumero = String(parseInt(values.numeroFichero) + 1).padStart(3, '0');
        form.setFieldsValue({ numeroFichero: nuevoNumero });
        setConfiguracion(prev => ({ ...prev, numeroFichero: nuevoNumero }));
      }
    } catch (error) {
      console.error('Error generando fichero:', error);
      if (error.response?.data?.detalles) {
        // Mostrar errores de validación
        Modal.error({
          title: 'Error al generar el fichero',
          content: (
            <div>
              <Text type="danger">No se puede generar el fichero debido a los siguientes errores:</Text>
              <ul style={{ marginTop: 10 }}>
                {error.response.data.detalles.map((err, idx) => (
                  <li key={idx}>{err}</li>
                ))}
              </ul>
            </div>
          ),
          width: 600
        });
      } else {
        message.error(error.response?.data?.error || 'Error al generar el fichero');
      }
    } finally {
      setGenerando(false);
    }
  };


  // Obtener opciones de filtro para nacionalidad
  const getNacionalidadFilters = () => {
    const naciones = {};
    ocupantes.forEach(item => {
      const n = item.nacion || 'Desconocida';
      naciones[n] = (naciones[n] || 0) + 1;
    });
    return Object.keys(naciones).map(key => ({
      text: `${key} (${naciones[key]})`,
      value: key
    }));
  };

  // Manejar cambio de tabla (filtros y ordenamiento)
  const handleTableChange = (pagination, filters, sorter) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);
  };

  // Columnas de la tabla - CORREGIDAS
  const columns = [
    {
      title: 'Reserva',
      dataIndex: 'NumReserva',
      key: 'NumReserva',
      width: 80,
      sorter: (a, b) => (a.NumReserva || 0) - (b.NumReserva || 0),
      sortOrder: sortedInfo.columnKey === 'NumReserva' && sortedInfo.order,
      render: (num) => (
        <Tag color="blue" style={{ fontWeight: 'bold' }}>
          #{num}
        </Tag>
      )
    },
    {
      title: 'Habitación',
      dataIndex: 'NumHab',
      key: 'NumHab',
      width: 90,
      render: (hab) => (
        <Tag color="green">{hab}</Tag>
      )
    },
    {
      title: 'Ocupante',
      dataIndex: 'NumOcupante',
      key: 'NumOcupante',
      width: 80,
      render: (num) => (
        <Badge count={num} showZero color="cyan" />
      )
    },
    {
      title: 'Nombre',
      key: 'nombreCompleto',
      width: 200,
      render: (_, record) => (
        <div>
          <Text strong>{record.Nombre1 || ''}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {record.Apellido1 || ''} {record.Apellido2 || ''}
          </Text>
        </div>
      )
    },
    {
      title: 'Documento',
      dataIndex: 'NIF',
      key: 'NIF',
      width: 140,
      render: (nif, record) => (
        <div>
          <Tag color={nif?.length > 8 ? 'orange' : 'purple'} style={{ fontFamily: 'monospace' }}>
            {nif || 'N/A'}
          </Tag>
          <br />
          <Text type="secondary" style={{ fontSize: '11px' }}>
            {record.TIpoDocumento || 'Sin especificar'}
          </Text>
        </div>
      )
    },
    {
      title: 'Nacionalidad',
      dataIndex: 'nacion',
      key: 'nacion',
      width: 130,
      render: (nacion) => (
        <Tag color={nacion === 'ESPAÑA' ? 'red' : 'blue'} icon={<FlagOutlined />}>
          {nacion || 'Desconocida'}
        </Tag>
      ),
      filters: getNacionalidadFilters(),
      filteredValue: filteredInfo.nacion || null,
      onFilter: (value, record) => record.nacion === value
    },
    {
      title: 'Entrada',
      dataIndex: 'Entrada',
      key: 'Entrada',
      width: 120,
      render: (fecha) => fecha ? new Date(fecha).toLocaleDateString() : '',
      sorter: (a, b) => new Date(a.Entrada || 0) - new Date(b.Entrada || 0),
      sortOrder: sortedInfo.columnKey === 'Entrada' && sortedInfo.order
    },
    {
      title: 'Salida',
      dataIndex: 'Salida',
      key: 'Salida',
      width: 120,
      render: (fecha) => fecha ? new Date(fecha).toLocaleDateString() : '',
      sorter: (a, b) => new Date(a.Salida || 0) - new Date(b.Salida || 0),
      sortOrder: sortedInfo.columnKey === 'Salida' && sortedInfo.order
    },
    {
      title: 'Días',
      dataIndex: 'dias',
      key: 'dias',
      width: 70,
      render: (dias) => (
        <Tag color="default">{dias || 0}</Tag>
      )
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: 100,
      fixed: 'right',
      render: (_, record) => (
        <Tooltip title="Ver detalles">
          <Button
            type="primary"
            icon={<EyeOutlined />}
            size="small"
            onClick={() => handleVerDetalle(record)}
          />
        </Tooltip>
      )
    }
  ];

  // Configuración del formulario
  const configForm = (
    <Form form={form} layout="inline" style={{ marginBottom: 16 }}>
      <Form.Item name="tipoPolicia" label="Formato">
        <Select style={{ width: 180 }} onChange={(value) => setConfiguracion(prev => ({ ...prev, tipoPolicia: value }))}>
          <Option value={0}>Mossos (Cataluña)</Option>
          <Option value={1}>Italia</Option>
        </Select>
      </Form.Item>
      
      <Form.Item name="numeroFichero" label="Nº Fichero">
        <Input
          style={{ width: 80 }}
          maxLength={3}
          onChange={(e) => {
            const val = e.target.value.replace(/\D/g, '');
            if (val.length <= 3) {
              form.setFieldsValue({ numeroFichero: val.padStart(3, '0') });
            }
          }}
        />
      </Form.Item>
      
      <Form.Item>
        <Button
          type="primary"
          icon={<DownloadOutlined />}
          onClick={handleGenerarFichero}
          loading={generando}
        >
          Generar Fichero
        </Button>
      </Form.Item>
      
    </Form>
  );

  // Barra de búsqueda
  const searchBar = (
    <div style={{ marginBottom: 16 }}>
      <Space wrap>
        <DatePicker
          value={fechaSeleccionada}
          onChange={handleCambioFecha}
          format="DD/MM/YYYY"
          placeholder="Seleccionar fecha"
          allowClear={false}
          style={{ width: 180 }}
        />
        <Button
          type="primary"
          icon={<SearchOutlined />}
          onClick={handleBuscar}
          loading={loading}
        >
          Buscar
        </Button>
        <Button
          icon={<ReloadOutlined />}
          onClick={() => cargarOcupantes()}
        >
          Actualizar
        </Button>
        {/* <Badge count={ocupantes.length} showZero color="blue">
          <span style={{ marginLeft: 8 }}>Ocupantes</span>
        </Badge> */}
      </Space>
    </div>
  );

  

  return (
    <>
      <PageContainer
        title="Registro de Ocupantes"
        subtitle={`Ocupantes - ${fechaSeleccionada.format('DD/MM/YYYY')}`}
        icon={<TeamOutlined />}
        loading={loading}
        onRefresh={() => cargarOcupantes()}
        badgeCount={ocupantes.length}
        badgeStatus="processing"
        // extra={
        //   <Space>
        //     <Tooltip title="Información del formato">
        //       <Button icon={<InfoCircleOutlined />}>
        //         Ayuda
        //       </Button>
        //     </Tooltip>
        //   </Space>
        // }
        cardStyle={{
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)'
        }}
      >
        {/* Configuración y acciones */}
        {configForm}
        

        
        {/* Barra de búsqueda */}
        {searchBar}
        
        {/* Tabla de ocupantes */}
        <Table
          columns={columns}
          dataSource={ocupantes}
          rowKey={(record) => `${record.NumReserva}-${record.NumOcupante}-${record.Linea}-${record.Codigo}`}
          loading={loading}
          onChange={handleTableChange}
          scroll={{ x: 1200, y: 500 }}
          style={{
            borderRadius: '8px',
            overflow: 'hidden'
          }}
          size="middle"
          locale={{
            emptyText: (
              <Empty
                description="No se encontraron ocupantes para esta fecha"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            )
          }}
          pagination={{
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} ocupantes`,
            pageSizeOptions: ['10', '20', '50', '100'],
            defaultPageSize: 20
          }}
        />
      </PageContainer>

      {/* Modal de detalle del ocupante */}
      <Modal
        title="Detalle del Ocupante"
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setModalVisible(false)}>
            Cerrar
          </Button>
        ]}
        width={700}
      >
        {detalleOcupante && (
          <div>
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <Card size="small" style={{ backgroundColor: '#f0f5ff' }}>
                  <Row>
                    <Col span={12}>
                      <Text strong>Reserva: </Text>
                      <Tag color="blue">#{detalleOcupante.NumReserva}</Tag>
                      <br />
                      <Text strong>Habitación: </Text>
                      <Tag color="green">{detalleOcupante.NumHab}</Tag>
                      <br />
                      <Text strong>Ocupante: </Text>
                      <Tag color="cyan">{detalleOcupante.NumOcupante}</Tag>
                    </Col>
                    <Col span={12}>
                      <Text strong>Entrada: </Text>
                      {detalleOcupante.Entrada && new Date(detalleOcupante.Entrada).toLocaleDateString()}
                      <br />
                      <Text strong>Salida: </Text>
                      {detalleOcupante.Salida && new Date(detalleOcupante.Salida).toLocaleDateString()}
                      <br />
                      <Text strong>Días: </Text>
                      {detalleOcupante.dias || 0}
                    </Col>
                  </Row>
                </Card>
              </Col>
            </Row>

            <Descriptions bordered column={{ xs: 1, sm: 2 }} style={{ marginTop: 16 }}>
              <Descriptions.Item label="Nombre">
                {detalleOcupante.Nombre1 || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Apellidos">
                {(detalleOcupante.Apellido1 || '') + ' ' + (detalleOcupante.Apellido2 || '')}
              </Descriptions.Item>
              <Descriptions.Item label="NIF / Documento">
                <Tag color="purple">{detalleOcupante.NIF || 'N/A'}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Tipo Documento">
                {detalleOcupante.TIpoDocumento || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Nacionalidad">
                <Tag color={detalleOcupante.nacion === 'ESPAÑA' ? 'red' : 'blue'}>
                  <FlagOutlined /> {detalleOcupante.nacion || 'Desconocida'}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Sexo">
                {detalleOcupante.sexo === '1' ? 'Masculino' : 
                 detalleOcupante.sexo === '2' ? 'Femenino' : 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Fecha Nacimiento" span={2}>
                {detalleOcupante.FechaNacimiento ? 
                  new Date(detalleOcupante.FechaNacimiento).toLocaleDateString() : 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Lugar Nacimiento" span={2}>
                {detalleOcupante.LugarNacimiento || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Dirección" span={2}>
                {detalleOcupante.Direccion || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Población" span={2}>
                {detalleOcupante.Poblacion || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Teléfono">
                {detalleOcupante.Telefono1 || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Email">
                {detalleOcupante.EMail || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Tipo Habitación">
                {detalleOcupante.TipoHab || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Régimen">
                {detalleOcupante.Regimen || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Tipo Factura" span={2}>
                {detalleOcupante.TipoFac || 'N/A'}
              </Descriptions.Item>
            </Descriptions>
          </div>
        )}
      </Modal>

      {/* Modal de resumen del fichero */}
      <Modal
        title="Resumen del Fichero Generado"
        open={resumenVisible}
        onCancel={() => setResumenVisible(false)}
        footer={[
          <Button key="close" type="primary" onClick={() => setResumenVisible(false)}>
            Aceptar
          </Button>
        ]}
        width={600}
      >
        <Alert
          message="Fichero generado correctamente"
          description={`Se han registrado ${ocupantes.length} ocupantes para el parte policial.`}
          type="success"
          showIcon
          style={{ marginBottom: 16 }}
        />
        
        <Card size="small" title="Resumen de ocupantes">
          <Row gutter={[16, 8]}>
            <Col span={12}>
              <Statistic
                title="Total"
                value={ocupantes.length}
                suffix="ocupantes"
                valueStyle={{ fontSize: 20 }}
              />
            </Col>
            {/* <Col span={12}>
              <Statistic
                title="Nacionalidades"
                value={Object.keys(estadisticas.nacionalidades).length}
                suffix="países"
                valueStyle={{ fontSize: 20 }}
              />
            </Col> */}
          </Row>
          
          <Divider style={{ margin: '12px 0' }} />
          
          {/* <Text strong>Nacionalidades más frecuentes:</Text>
          <div style={{ marginTop: 8 }}>
            {Object.entries(estadisticas.nacionalidades)
              .sort((a, b) => b[1] - a[1])
              .slice(0, 5)
              .map(([key, val]) => (
                <Tag key={key} color={key === 'ESPAÑA' ? 'red' : 'blue'} style={{ margin: 4 }}>
                  {key}: {val}
                </Tag>
              ))}
          </div> */}
        </Card>
      </Modal>
    </>
  );
};

export default RegistroOcupantes;